import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Save } from 'lucide-react';
import CdCSelector from '../components/CdCSelector';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const STATO_OPTIONS = ['aperta', 'chiusa', 'sospesa'];

interface Commessa {
  id: string;
  codice: string;
  nome: string;
  descrizione: string | null;
  stato: string;
  budget: number | null;
  data_inizio: string | null;
  data_fine: string | null;
  centro_costo_default_id: string | null;
}

const CommessaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [commessa, setCommessa] = useState<Commessa | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [stato, setStato] = useState('aperta');
  const [budget, setBudget] = useState('');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [centroCostoDefaultId, setCentroCostoDefaultId] = useState<string | null>(null);

  const caricaDati = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/centri-costo/commesse/${id}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore caricamento commessa: ${res.status}`);
      const data: Commessa = await res.json();
      setCommessa(data);
      setNome(data.nome);
      setDescrizione(data.descrizione ?? '');
      setStato(data.stato);
      setBudget(data.budget != null ? String(data.budget) : '');
      setDataInizio(data.data_inizio ? data.data_inizio.slice(0, 10) : '');
      setDataFine(data.data_fine ? data.data_fine.slice(0, 10) : '');
      setCentroCostoDefaultId(data.centro_costo_default_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    caricaDati();
  }, [caricaDati]);

  const handleSave = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const payload: Record<string, unknown> = {
        nome,
        descrizione: descrizione || null,
        stato,
        centro_costo_default_id: centroCostoDefaultId,
      };
      if (budget) payload.budget = parseFloat(budget);
      if (dataInizio) payload.data_inizio = dataInizio;
      if (dataFine) payload.data_fine = dataFine;

      const res = await fetch(`/api/v1/centri-costo/commesse/${id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Errore salvataggio: ${res.status}`);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  }, [id, nome, descrizione, stato, budget, dataInizio, dataFine, centroCostoDefaultId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Caricamento…
      </div>
    );
  }

  if (error || !commessa) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error ?? 'Commessa non trovata'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          className="rounded-md p-1.5 hover:bg-gray-100"
          onClick={() => navigate('/centri-costo/commesse')}
        >
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">{commessa.codice} — {commessa.nome}</h1>
          <p className="text-sm text-gray-500">{commessa.stato}</p>
        </div>
      </div>

      {/* Feedback */}
      {saveSuccess && (
        <div className="rounded-md border border-green-300 bg-green-50 px-4 py-2 text-sm text-green-700">
          Salvato con successo
        </div>
      )}
      {saveError && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">
          {saveError}
        </div>
      )}

      {/* Form */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Dati commessa</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome</label>
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Stato</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={stato}
              onChange={(e) => setStato(e.target.value)}
            >
              {STATO_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Budget (€)</label>
            <input
              type="number"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Centro costo default</label>
            <CdCSelector
              value={centroCostoDefaultId}
              onChange={setCentroCostoDefaultId}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data inizio</label>
            <input
              type="date"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={dataInizio}
              onChange={(e) => setDataInizio(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data fine</label>
            <input
              type="date"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={dataFine}
              onChange={(e) => setDataFine(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Descrizione</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={3}
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            <Save className="h-4 w-4" />
            {saving ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommessaDetail;
